import React, { useState, useEffect } from "react";
import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useTrailDescriptionFeedback, FeedbackType } from "@/hooks/useTrailDescriptionFeedback";

interface TrailDescriptionFeedbackProps {
  trailId: string;
  descriptionId: string;
  variant?: "default" | "compact";
}

const FEEDBACK_OPTIONS: { value: FeedbackType; label: string; placeholder: string }[] = [
  {
    value: "helpful",
    label: "Helpful",
    placeholder: "What did you find most useful about this description?",
  },
  {
    value: "not_helpful",
    label: "Not helpful",
    placeholder: "What was missing or unclear?",
  },
  {
    value: "inaccurate",
    label: "Inaccurate",
    placeholder: "What information was incorrect? Please be specific.",
  },
];

export default function TrailDescriptionFeedback({
  trailId,
  descriptionId,
  variant = "default",
}: TrailDescriptionFeedbackProps) {
  const [open, setOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(null);
  const [feedbackText, setFeedbackText] = useState("");

  const { existingFeedback, isLoading, isSubmitting, isRegenerating, submitFeedback, isAuthenticated } =
    useTrailDescriptionFeedback(descriptionId);

  // Pre-fill form with existing feedback when dialog opens
  useEffect(() => {
    if (open && existingFeedback) {
      setFeedbackType(existingFeedback.feedback_type);
      setFeedbackText(existingFeedback.feedback_text);
    } else if (!open) {
      // Reset form when dialog closes (if no existing feedback)
      if (!existingFeedback) {
        setFeedbackType(null);
        setFeedbackText("");
      }
    }
  }, [open, existingFeedback]);

  const handleSubmit = async () => {
    if (!feedbackType || feedbackText.length < 10) return;

    const result = await submitFeedback(trailId, feedbackType, feedbackText);
    if (result.success) {
      setOpen(false);
      
      // If regeneration was triggered and description was updated, 
      // the parent component will refresh via the hook
      if (result.regenerationTriggered) {
        // Reset form since feedback was cleared
        setFeedbackType(null);
        setFeedbackText("");
      }
    }
  };

  const selectedOption = FEEDBACK_OPTIONS.find((opt) => opt.value === feedbackType);
  const isValid = feedbackType && feedbackText.length >= 10;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {variant === "compact" ? (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" disabled={isRegenerating}>
            <MessageSquare className="h-3 w-3 mr-1" />
            {isRegenerating ? "Updating..." : existingFeedback ? "Edit Feedback" : "Feedback"}
          </Button>
        ) : (
          <Button variant="outline" size="sm" className="gap-2" disabled={isRegenerating}>
            <MessageSquare className="h-4 w-4" />
            {isRegenerating ? "Updating..." : existingFeedback ? "Edit Feedback" : "Give Feedback"}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Trail Description Feedback</DialogTitle>
          <DialogDescription>
            {existingFeedback
              ? "Update your feedback about this trail description."
              : "Help us improve by sharing your thoughts about this description."}
          </DialogDescription>
        </DialogHeader>

        {!isAuthenticated ? (
          <div className="py-4 text-center text-muted-foreground">
            Please sign in to submit feedback.
          </div>
        ) : isLoading ? (
          <div className="py-8 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <Label>How would you rate this description?</Label>
              <RadioGroup
                value={feedbackType || ""}
                onValueChange={(value) => setFeedbackType(value as FeedbackType)}
                className="space-y-2"
              >
                {FEEDBACK_OPTIONS.map((option) => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={option.value} id={option.value} />
                    <Label htmlFor={option.value} className="font-normal cursor-pointer">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-text">
                Tell us more <span className="text-muted-foreground">(required, min 10 characters)</span>
              </Label>
              <Textarea
                id="feedback-text"
                placeholder={selectedOption?.placeholder || "Please select an option above first..."}
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                className="min-h-[100px]"
                disabled={!feedbackType}
              />
              {feedbackText.length > 0 && feedbackText.length < 10 && (
                <p className="text-xs text-destructive">
                  {10 - feedbackText.length} more characters needed
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isSubmitting || !isAuthenticated}>
            {isSubmitting
              ? "Submitting..."
              : existingFeedback
              ? "Update Feedback"
              : "Submit Feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
